<template>
  <section>
    <errors-list :errorMessage="errorMessage" />
    <v-data-table
      :headers="translatedHeaders"
      :items="items"
      :loading="loading"
      :rows-per-page-items="rowsPerPageItems"
      :total-items="pagination.totalItems"
      :pagination.sync="pagination"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <td>
            <v-btn class="ma-0" flat small :to="`/contract/${props.item.insuranceInternalId}`">
              {{ props.item.insuranceInternalId }}
            </v-btn>
          </td>

          <grid-cell
            :header="header"
            :row="props.item"
            v-for="(header, index) in translatedHeaders.slice(1)"
            :key="index"
          ></grid-cell>
        </tr>
      </template>
    </v-data-table>
  </section>
</template>

<script>
import GridCell from '../../components/GridCell';
import { serializeQuery, defaultDateTimeFormatter } from '../../utils';
import { getErrorMessage } from '../../errorHandlers';
import {
  contractStatusLabel,
  bankDetailsStatus,
  getMandateInformationCode,
  getMandateCommentCode,
} from '../../constants';
import ErrorsList from '../../components/ErrorsList';

export const defaultHeaders = [
  {
    text: '@insurancesList/contractIdColumnHeader',
    value: 'insuranceInternalId',
    width: 200,
    sortable: true,
  },
  {
    text: '@insurancesList/productNameColumnHeader',
    align: 'left',
    value: 'insuranceProductName',
    sortable: true,
  },

  {
    text: '@insurance/bankDetails/contractStatusLabel',
    value: 'insuranceStatus',
    sortable: false,
    format(val) {
      return this.$t(contractStatusLabel[val]);
    },
  },
  {
    text: '@insurance/bankDetails/insuranceCreatedLabel',
    value: 'insuranceCreated',
    sortable: true,
    format(val) {
      return val ? defaultDateTimeFormatter(val) : '-';
    },
  },
  {
    text: '@insurance/bankDetails/bgcStatusUpdatedLabel',
    value: 'bgcUpdated',
    sortable: false,
    format(val) {
      return val ? defaultDateTimeFormatter(val) : '-';
    },
  },
  {
    text: '@insurance/bankDetails/bgcStatusLabel',
    value: 'bgcStatus',
    sortable: false,
    format(val) {
      return val ? this.$t(bankDetailsStatus[val]) : '-';
    },
  },
  {
    text: '@insurance/bankDetails/bgcInformationCodeLabel',
    value: 'bgcInformationCode',
    sortable: false,
    format(val) {
      return val ? getMandateInformationCode(val) : '-';
    },
  },
  {
    text: '@insurance/bankDetails/bgcCommentCodeLabel',
    value: 'bgcCommentCode',
    sortable: false,
    format(val) {
      return val ? getMandateCommentCode(val) : '-';
    },
  },
];

export const sortFieldMap = {
  insuranceInternalId: 'i.internalId',
  insuranceProductName: 'p.name',
  insuranceCreated: 'i.created',
};

export default {
  components: { GridCell, ErrorsList },

  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
    headers: {
      type: Array,
      default: () => defaultHeaders,
    },
  },

  data() {
    return {
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 15,
        sortBy: 'insuranceCreated',
        totalItems: 0,
      },
      rowsPerPageItems: [10, 15, 50, 100],
      items: [],
      loading: false,
      errorMessage: '',
      lastQueryString: '',
    };
  },

  watch: {
    filters() {
      this.fetchData();
    },

    pagination() {
      this.fetchData();
    },
  },

  computed: {
    translatedHeaders() {
      return Array.isArray(this.headers)
        ? this.headers.map((h) => ({
            ...h,
            text: this.$t(h.text),
          }))
        : [];
    },

    query() {
      const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const take = this.pagination.rowsPerPage;
      const query = {
        status: 'approved',
        ...this.filters,
        skip,
        take,
      };

      if (this.pagination.sortBy) {
        query.sortBy = sortFieldMap[this.pagination.sortBy];
        query.sortOrder = this.pagination.descending ? 'DESC' : 'ASC';
        return query;
      } else {
        // eslint-disable-next-line no-unused-vars
        const { sortBy, sortOrder, ...rest } = query;
        return rest;
      }
    },
  },

  methods: {
    async fetchData() {
      const qs = serializeQuery(this.query);
      if (this.lastQueryString === qs || this.loading) {
        return;
      }

      try {
        this.loading = true;
        this.$emit('beforeRequest', this.query);
        const {
          data: { data, meta },
        } = await this.$http.get(`/api/bankdetails?${qs}`);
        this.items = data;
        this.pagination.totalItems = meta.total;
        this.lastQueryString = qs;
        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
